import {
  areaOverviewEndpoint,
  bopisEndpoint,
  bopisLeadTimeEndpoint,
  rfidApprovalsEndpoint,
  rfidCountEndpoint,
  rfidEndpoint,
  rfidOverviewEndpoint,
  rfidSalesRetaggingEndpoint,
  stockAccuracyEndpoint,
  stockInventoryCountEndpoint,
} from '../constants';
import { useAuthAxiosRequest } from './useAuthAxiosRequest';
import { useUser } from './useController';
import { LastUpdatedTime, StorePerformanceItem, StorePerformanceRequest } from './useTransaction';

export type RfidSalesRetaggingResponse = LastUpdatedTime & {
  retaggingRate: number;
  addedSales: number;
  totalExpectedRetagging: number;
  totalSuccessfulRetagging: number;
  totalAddedSalesValue: number;
  totalSalesValue: number;
};

export type RfidApprovalsResponse = LastUpdatedTime & {
  negative: {
    approvedPieces: number;
    difference: number;
    missingPieces: number;
    totalPieces: number;
    approvalRate: number;
    totalApproval: number;
    totalApprovalLength: number;
  };
  positive: {
    approvedPieces: number;
    difference: number;
    extraPieces: number;
    totalPieces: number;
    approvalRate: number;
    totalApproval: number;
    totalApprovalLength: number;
  };
  totalCounts: number;
};

export type RfidCountResponse = LastUpdatedTime & {
  counts: number;
  timeSpent: number;
  scannersUsed: number;
};

export type StockAccuracyItem = {
  totalStockCount: number;
  positivePieces: number;
  negativePieces: number;
  openOrderQuantity: number;
  date: string;
};

export type StockAccuracyResponse = LastUpdatedTime & {
  stockAccuracy: StockAccuracyItem[];
};

export type StockStatusType = 'Pick' | 'Picked' | 'NotFound';

export type StockCountItem = {
  articleId: string;
  productName: string;
  productId: string;
  index: string;
  subIndex: string;
  departmentName: string;
  departmentNumber: string;
  status: StockStatusType;
  sicScope: string;
  season: number;
  lastUpdatedTimestamp: string;
};

export type StockInventoryCountResponse = {
  startDate: string;
  endDate: string;
  counts: StockCountItem[];
};

export type StockInventoryCountUpdatedItems = {
  sicScope: string;
  status: StockStatusType;
};

export type RfidOverviewResponse = {
  negativeApprovalRate: number;
  positiveApprovalRate: number;
  rfidSales: number;
  retaggingRate: number;
  rfidCounts: number;
  timeSpent: number;
  devices: number;
  stockAccuracy: number;
};

export type BopisData = LastUpdatedTime & {
  pending: {
    notStarted: number;
    partiallyPicked: number;
    fullyPicked: number;
  };
  processed: {
    fullyFulfilled: number;
    partiallyFulfilled: number;
    cancelled: number;
    zeroPicked: number;
  };
  bopisNetSales: number;
  totalNetSales: number;
  convertedLoyaltyCustomers: number;
  totalLoyaltyCustomers: number;
  upsellingValue: number;
};

export type BopisLeadTimePerformanceItem = StorePerformanceItem & {
  compliantOrders: number;
  totalOrders: number;
  totalMinutesSpent: number;
};

export type BopisLeadTimeData = LastUpdatedTime & {
  items: BopisLeadTimePerformanceItem[];
  compliantOrders: number;
  totalOrders: number;
  totalMinutesSpent: number;
  pickMinutes: number;
  packMinutes: number;
};

export const useRfid = () => {
  const {
    get: { currentStoreId, keyToken },
  } = useUser();

  const { authAxiosRequest } = useAuthAxiosRequest(keyToken);

  const fetchRfidCountData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<RfidCountResponse>(
      `${rfidEndpoint}/${currentStoreId}${rfidCountEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchRfidSalesRetaggingData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<RfidSalesRetaggingResponse>(
      `${rfidEndpoint}/${currentStoreId}${rfidSalesRetaggingEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchRfidApprovalsData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<RfidApprovalsResponse>(
      `${rfidEndpoint}/${currentStoreId}${rfidApprovalsEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchStockInventoryCount = async (date: string) => {
    const response = await authAxiosRequest<StockInventoryCountResponse>(
      `${rfidEndpoint}/${currentStoreId}${stockInventoryCountEndpoint}`,
      {
        params: {
          date,
        },
      },
    );

    return response;
  };

  const updateStockInventoryCount = async (date: string, updatedItems: StockInventoryCountUpdatedItems[]) => {
    const request = {
      set: updatedItems,
    };
    const response = await authAxiosRequest<StockInventoryCountResponse>(
      `${rfidEndpoint}/${currentStoreId}${stockInventoryCountEndpoint}`,
      {
        method: 'PATCH',
        data: request,
        params: {
          date,
        },
      },
    );

    return response;
  };

  const fetchStockAccuracyData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<StockAccuracyResponse>(
      `${rfidEndpoint}/${currentStoreId}${stockAccuracyEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchBopisData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<BopisData>(`${rfidEndpoint}/${currentStoreId}${bopisEndpoint}`, {
      params: request,
    });

    return response;
  };

  const fetchBopisLeadTimeData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<BopisLeadTimeData>(
      `${rfidEndpoint}/${currentStoreId}${bopisLeadTimeEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  // AREA OVERVIEW WIDGETS CALLS
  const fetchRfidOverviewData = async (request: StorePerformanceRequest) => {
    const rfidOverviewResponse = await authAxiosRequest<RfidOverviewResponse>(
      `${rfidEndpoint}/${currentStoreId}${areaOverviewEndpoint}${rfidOverviewEndpoint}`,
      {
        params: request,
      },
    );
    return rfidOverviewResponse;
  };

  return {
    fetchRfidCountData,
    fetchRfidSalesRetaggingData,
    fetchRfidApprovalsData,
    fetchStockInventoryCount,
    updateStockInventoryCount,
    fetchStockAccuracyData,
    fetchBopisData,
    fetchBopisLeadTimeData,
    // Area Overview Widgets
    fetchRfidOverviewData,
  };
};
